/**
 * SSE connection lifecycle hook.
 *
 * Connects to GET /api/v1/sse/stream with JWT auth, auto-reconnects with
 * exponential backoff + jitter, heartbeat timeout, 30-min TTL for token
 * refresh, and foreground/background handling via @capacitor/app.
 */

import { useEffect, useRef, useState } from 'react';
import { EventStreamContentType, fetchEventSource } from '@microsoft/fetch-event-source';
import { App } from '@capacitor/app';
import { getApiBaseUrl } from '../config/api';
import {
  SSE_EVENT_TYPE,
  isSSEEventType,
  type SSEEvent,
  type SSEEventDataMap,
  type SSEEventType,
} from '../config/sseEventTypes';
import { getAuthToken } from '@api-client/api-client';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { logger } from '@utils/logger';

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * `'fatal'` is reserved for the terminal state where the connection has been
 * abandoned (non-retriable 4xx after the 401 retry budget). The hook
 * recovers on foreground via the appStateChange handler, which resets state
 * and calls `connect()` again. Consumers can use `'fatal'` to surface a
 * "you're disconnected from real-time updates" UI signal.
 */
export type ConnectionState =
  'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'fatal';

// `SSEEvent` is now defined in `src/config/sseEventTypes.ts` as a
// discriminated union over `SSE_EVENT_TYPE`. Re-export it here so existing
// consumers that import from `@hooks/useEventStream` keep working.
export type { SSEEvent };

interface UseEventStreamOptions {
  /** Only connect when true (e.g., user is authenticated) */
  enabled: boolean;
  /** Called for every SSE event (invalidation, data, full-sync) */
  onEvent: (event: SSEEvent) => void;
}

// ── Constants ────────────────────────────────────────────────────────────────

const SSE_ENDPOINT = 'api/v1/sse/stream';
// HEARTBEAT_TIMEOUT_MS, CONNECTION_TTL_MS, and MAX_RETRY_MS are exported solely
// so the connection-lifecycle tests (webapp#681) drive their fake timers off the
// hook's real values rather than hand-copied mirrors that drift silently when a
// timing constant changes here. They are not part of the hook's public consumer
// surface (same rationale as the exported `getRetryDelay` below).
export const HEARTBEAT_TIMEOUT_MS = 45_000; // 3× server heartbeat interval (15s)
export const CONNECTION_TTL_MS = 30 * 60 * 1000; // 30 min — force reconnect for JWT refresh
const INITIAL_RETRY_MS = 1_000;
export const MAX_RETRY_MS = 30_000;
const MAX_NO_TOKEN_RETRIES = 10; // Stop retrying if auth token unavailable after this many attempts
// Foreground transitions within this window are treated as "no events missed" (e.g.
// a quick browser tab-flick) and skip the FULL_SYNC cache nuke that error reconnects
// trigger. Above this window, FULL_SYNC fires as a safety net for missed events.
const FOREGROUND_DEBOUNCE_MS = 10_000;

/**
 * Sentinel error for terminal SSE failures. Thrown for non-retriable 4xx
 * responses: any 4xx except 429 (rate-limited, retriable) and 401 while
 * still within the retry budget (retriable for token-refresh races).
 * Once the 401 retry budget is exhausted, 401 also falls through to this
 * terminal error, so "401" is not a permanent exclusion.
 *
 * `status` is exposed as a property so consumers (e.g. analytics) can
 * distinguish 403 from 404 without re-parsing the message string.
 */
class FatalSSEError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'FatalSSEError';
    this.status = status;
  }
}

/**
 * Exponential backoff with ±25% jitter. First attempt (attempt=1) → ~1s base.
 * Exported for unit testing of the jitter bounds (webapp#681); not part of the
 * hook's public consumer surface.
 */
export function getRetryDelay(attempt: number): number {
  const exponent = Math.max(0, attempt - 1);
  const base = Math.min(INITIAL_RETRY_MS * 2 ** exponent, MAX_RETRY_MS);
  return base * (0.75 + Math.random() * 0.5);
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useEventStream({ enabled, onEvent }: UseEventStreamOptions) {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');

  // Ref so the latest callback is always used without re-running the effect.
  // Updated via useEffect (not assigned during render) to satisfy Rules of React's
  // purity rule and the React Compiler. The `[onEvent]` dep means we only re-sync
  // when the parent passes a new function reference; for inline callbacks that's
  // every render anyway, but a useCallback'd parent gets the work it deserves.
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  // Telemetry. The effect's deps are intentionally `[enabled]` only (any
  // other dep would tear down and re-establish the SSE connection on every
  // render). Capturing `trackEvent` in a ref keeps the closure pointing at
  // the latest stable reference without making the hook re-run. Updated via
  // useEffect (not assigned during render) so render stays pure under the
  // React Compiler — same pattern as `onEventRef` above.
  const { trackEvent } = useAmplitudeTracking();
  const trackEventRef = useRef(trackEvent);
  useEffect(() => {
    trackEventRef.current = trackEvent;
  }, [trackEvent]);

  useEffect(() => {
    if (!enabled) {
      setConnectionState('disconnected');
      return;
    }

    let stopped = false;
    let currentCtrl: AbortController | null = null;
    let heartbeatTimer: ReturnType<typeof setTimeout> | undefined;
    let ttlTimer: ReturnType<typeof setTimeout> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryCount = 0;
    // Dedicated budget for server-side 401 responses only — isolated from the
    // no-token retry path and generic connection-error retries, both of which
    // increment `retryCount`. Without this split, as few as 3 no-token retries
    // could exhaust the 401 budget before a single real 401 ever arrived,
    // and every non-401 error would eat into it too.
    let authRetryCount = 0;
    let hasEverConnected = false;
    // Tracks whether the current reconnect is "safe to skip FULL_SYNC for":
    // - planned TTL refresh (stream healthy until disconnect, no events missed)
    // - quick foreground bounce (< FOREGROUND_DEBOUNCE_MS in background, see below)
    // vs. an error/heartbeat timeout (events may have been missed → full-sync needed)
    let isSafeReconnect = false;
    // Timestamp set when the app goes to background. On foreground, if elapsed
    // time is short, we skip FULL_SYNC (no realistic event loss for a tab-flick).
    // Long backgrounds still emit FULL_SYNC as a safety net.
    let lastBackgroundedAt = 0;
    // Gates the no-token cooldown log + Amplitude emit so they fire once per
    // cooldown entry. Without it, both would fire every retry tick because
    // `retryCount` stays pinned at MAX_NO_TOKEN_RETRIES inside the branch.
    // Reset on successful onopen and foreground transition so a fresh cooldown
    // does emit again.
    let hasReportedNoTokenCooldown = false;

    // ── Timer helpers ──────────────────────────────────────────────────────

    const clearTimers = () => {
      clearTimeout(heartbeatTimer);
      clearTimeout(ttlTimer);
      clearTimeout(retryTimer);
    };

    const scheduleConnect = (delayMs: number) => {
      if (stopped) return;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => void connect(), delayMs);
    };

    const resetHeartbeat = () => {
      clearTimeout(heartbeatTimer);
      heartbeatTimer = setTimeout(() => {
        if (stopped) return;
        logger.warn('SSE: heartbeat timeout — reconnecting');
        currentCtrl?.abort();
        scheduleConnect(0);
      }, HEARTBEAT_TIMEOUT_MS);
    };

    // ── Connection loop ────────────────────────────────────────────────────

    async function connect() {
      if (stopped) return;

      // Tear down any previous connection
      currentCtrl?.abort();
      clearTimers();

      const ctrl = new AbortController();
      currentCtrl = ctrl;

      setConnectionState(hasEverConnected ? 'reconnecting' : 'connecting');

      // Acquire auth token
      const token = await getAuthToken().catch(() => null);

      if (!token) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- stopped may change during await
        if (!stopped) {
          // If this retry path is hit during a TTL reconnect (token refresh race), events
          // may have been missed while the stream was dark — force full-sync on next onopen.
          // Applies to cooldown reconnects too (any time we re-enter with a newly-acquired
          // token after a no-token gap, events could have been missed).
          isSafeReconnect = false;

          if (retryCount >= MAX_NO_TOKEN_RETRIES) {
            // Cooldown mode: keep probing at max retry delay so SSE can self-heal if
            // the token source recovers (transient SDK / network blip). Don't bump
            // `retryCount` further. Stay pinned at MAX_NO_TOKEN_RETRIES so the next
            // no-token sample keeps landing here instead of spiralling the backoff.
            // Both the log and the analytics event are gated by
            // `hasReportedNoTokenCooldown` so they fire once per cooldown entry
            // instead of once per retry tick (the branch re-runs every tick).
            if (!hasReportedNoTokenCooldown) {
              logger.warn('SSE: max no-token retries reached, entering cooldown retry mode');
              trackEventRef.current(ANALYTICS_EVENTS.sse_no_token_cooldown, {
                retry_count: retryCount,
              });
              hasReportedNoTokenCooldown = true;
            }
            setConnectionState('disconnected');
            scheduleConnect(getRetryDelay(MAX_NO_TOKEN_RETRIES));
            return;
          }

          logger.debug('SSE: no auth token available, retrying…');
          retryCount++;
          scheduleConnect(getRetryDelay(retryCount));
        }
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- may change during await
      if (stopped || ctrl.signal.aborted) return;

      const url = `${getApiBaseUrl()}${SSE_ENDPOINT}`;

      try {
        await fetchEventSource(url, {
          signal: ctrl.signal,
          headers: { Authorization: `Bearer ${token}` },
          // We handle visibility ourselves via @capacitor/app
          openWhenHidden: true,

          // eslint-disable-next-line @typescript-eslint/require-await -- library type requires async
          async onopen(response) {
            if (
              response.ok &&
              response.headers.get('content-type')?.includes(EventStreamContentType)
            ) {
              const isReconnect = hasEverConnected;
              hasEverConnected = true;
              retryCount = 0;
              authRetryCount = 0;
              hasReportedNoTokenCooldown = false;
              setConnectionState('connected');

              // On error/heartbeat reconnect, refetch everything (events may have been missed).
              // On planned TTL reconnect, skip full-sync (stream was healthy until disconnect).
              if (isReconnect && !isSafeReconnect) {
                onEventRef.current({ type: SSE_EVENT_TYPE.FULL_SYNC, data: {} });
              }
              isSafeReconnect = false;

              resetHeartbeat();

              // Force reconnect after TTL so the next connection gets a fresh JWT.
              // Set isSafeReconnect so the next onopen skips full-sync (no events missed).
              ttlTimer = setTimeout(() => {
                if (stopped) return;
                logger.info('SSE: TTL reached — reconnecting for token refresh');
                isSafeReconnect = true;
                ctrl.abort();
                scheduleConnect(0);
              }, CONNECTION_TTL_MS);

              return;
            }

            // 401 may be a transient token-refresh race (Logto OIDC) — allow a few
            // retries with a fresh token before giving up. Each retry calls getAuthToken()
            // which returns the latest token from the auth layer. Increment the
            // dedicated `authRetryCount` here (not the generic catch-block ++) so
            // only actual 401 responses consume this budget.
            if (response.status === 401 && authRetryCount < 3) {
              authRetryCount++;
              throw new Error(`SSE: 401 — retrying with fresh token (attempt ${authRetryCount})`);
            }
            // Other 4xx (except 429) → non-retriable, stop reconnecting entirely
            if (response.status >= 400 && response.status < 500 && response.status !== 429) {
              throw new FatalSSEError(
                response.status,
                `SSE: non-retriable ${response.status} ${response.statusText}`,
              );
            }
            throw new Error(`SSE: server error ${response.status}`);
          },

          onmessage(msg) {
            // Reset heartbeat watchdog on every message (including heartbeat events).
            resetHeartbeat();

            // Filter out heartbeat events — they exist solely to reset the watchdog
            // and keep nginx/Cloudflare from closing idle connections.
            if (msg.event === 'heartbeat') return;
            // Server sends "connected" as a handshake confirmation — skip
            if (msg.event === SSE_EVENT_TYPE.CONNECTED) return;
            // Skip any messages with no event type and no data
            if (!msg.event && !msg.data) return;

            let data: Record<string, unknown>;
            try {
              data = msg.data ? (JSON.parse(msg.data) as Record<string, unknown>) : {};
            } catch {
              logger.error('SSE: failed to parse event data', msg.event, msg.data);
              return;
            }

            // Validate the event type against the known set before
            // dispatching. The `SSEEvent` discriminated union assumes the
            // type is one of `SSE_EVENT_TYPE`, so consumers can switch over
            // it and have `event.data` narrow correctly. Backend additions
            // that haven't shipped frontend support yet land here.
            const rawEventType = msg.event || '';
            if (!isSSEEventType(rawEventType)) {
              logger.debug('SSE: unknown event type, skipping', rawEventType);
              return;
            }
            const eventType: SSEEventType = rawEventType;

            try {
              // Cast the parsed JSON `data` to the per-type shape from
              // `SSEEventDataMap`. We just validated `eventType` is a known
              // discriminator, so this is safe in the wire-contract sense
              // (backend may still ship a partial payload, which the
              // typed shapes accommodate by marking fields optional).
              onEventRef.current({
                type: eventType,
                data: data as SSEEventDataMap[typeof eventType],
                id: msg.id || undefined,
              } as SSEEvent);
            } catch (handlerErr) {
              logger.error(`SSE: event handler error (${eventType})`, handlerErr);
              // Production observability. A handler that throws silently
              // drifts the UI from server state with no signal today. Carries
              // `event_type` so a regression in one handler is distinguishable
              // from a backend payload schema change affecting every type.
              trackEventRef.current(ANALYTICS_EVENTS.sse_handler_error, {
                event_type: eventType,
                error_message:
                  handlerErr instanceof Error ? handlerErr.message : String(handlerErr),
              });
            }
          },

          onclose() {
            // Server closed the stream — throw so onerror fires → promise rejects
            throw new Error('SSE: stream closed by server');
          },

          onerror(err) {
            clearTimeout(heartbeatTimer);
            clearTimeout(ttlTimer);
            // Always throw to disable the library's internal retry.
            // All reconnection logic is handled by our own scheduleConnect().
            throw err;
          },
        });
      } catch (err) {
        // fetchEventSource rejected (onerror threw, or network failure).
        // If we intentionally aborted (heartbeat, TTL, background), scheduleConnect
        // was already called from the timer — bail out here.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- may change during await
        if (stopped || ctrl.signal.aborted) return;

        // Non-retriable HTTP errors (403/404) → stop reconnecting entirely.
        // 401 is allowed a few retries (token-refresh race) before becoming fatal.
        if (err instanceof FatalSSEError) {
          logger.error(`SSE: fatal error, not retrying. ${err.message}`);
          // Production observability. The user is now silently disconnected
          // from real-time updates with no automatic recovery (polling
          // fallback was removed in webapp#471). EventStreamProvider reads
          // `'fatal'` to surface a user-facing toast.
          trackEventRef.current(ANALYTICS_EVENTS.sse_fatal_disconnect, {
            error_code: err.status,
            error_message: err.message,
          });
          setConnectionState('fatal');
          return;
        }

        retryCount++;
        const delay = getRetryDelay(retryCount);
        logger.warn(`SSE: error, retrying in ${Math.round(delay)}ms (attempt ${retryCount})`);
        setConnectionState('reconnecting');
        isSafeReconnect = false;
        scheduleConnect(delay);
      }
    }

    // ── App state: foreground / background ─────────────────────────────────
    // @capacitor/app works on web (visibilitychange), iOS, and Android.
    // Note: App.addListener is async but connect() is called before .then() resolves.
    // This is safe because: (1) the stopped guard in the callback prevents stale actions,
    // (2) the .then() handler checks stopped and calls handle.remove() if cleanup ran first.

    let removeAppStateListener: (() => void) | null = null;

    void App.addListener('appStateChange', ({ isActive }) => {
      if (stopped) return;

      if (isActive) {
        // Returning to foreground — reset state so the reconnect starts fresh:
        // - retryCount=0: first failure uses short delay, not pre-background backoff
        // - authRetryCount=0: fresh session gets a fresh 401 budget
        // - isSafeReconnect: only fire FULL_SYNC on long backgrounds (see below)
        // - hasReportedNoTokenCooldown=false: a fresh foreground cooldown re-emits
        retryCount = 0;
        authRetryCount = 0;
        // Suppress FULL_SYNC for short bounces (browser tab-flicks on web fire
        // visibilitychange ↔ appStateChange constantly). Without this, every
        // tab-switch on web would invalidate the entire React Query cache —
        // strictly worse than the polling we replaced. Long backgrounds still
        // emit FULL_SYNC as a safety net for genuinely missed events.
        const wasShortBackground =
          lastBackgroundedAt > 0 && Date.now() - lastBackgroundedAt < FOREGROUND_DEBOUNCE_MS;
        isSafeReconnect = wasShortBackground;
        lastBackgroundedAt = 0;
        hasReportedNoTokenCooldown = false;
        void connect();
      } else {
        // Going to background — disconnect to save battery / avoid stale connections
        lastBackgroundedAt = Date.now();
        currentCtrl?.abort();
        clearTimers();
        setConnectionState('disconnected');
      }
    })
      .then(handle => {
        if (stopped) {
          void handle.remove();
        } else {
          removeAppStateListener = () => void handle.remove();
        }
      })
      .catch((err: unknown) => {
        // On native, listener-registration failure means SSE never disconnects
        // when the app backgrounds. Battery drain rather than data loss, but
        // still worth promoting to `error` and surfacing to Amplitude so it's
        // visible in production telemetry rather than only in client logs.
        logger.error('SSE: failed to register appStateChange listener', err);
        trackEventRef.current(ANALYTICS_EVENTS.sse_app_listener_failed, {
          error_message: err instanceof Error ? err.message : String(err),
        });
      });

    // ── Initial connection ─────────────────────────────────────────────────
    void connect();

    // ── Cleanup ────────────────────────────────────────────────────────────
    return () => {
      stopped = true;
      currentCtrl?.abort();
      clearTimers();
      // Don't setConnectionState('disconnected') here — on dependency change,
      // the next effect run either enters the `!enabled` early return (which
      // sets 'disconnected') or calls `connect()` (which sets 'connecting').
      // On unmount, there's nothing to render either way. Avoids a redundant
      // state update.
      removeAppStateListener?.();
    };
  }, [enabled]);

  return { connectionState };
}
